import { Component, EventEmitter, Output, Input, ChangeDetectionStrategy } from '@angular/core';
import { CommonModule } from '@angular/common';

/**
 * ImageUploadComponent provides a drag-and-drop and file-browse interface for selecting image (and PDF) files with client-side validation, optional preview and file name display.
 * It handles file type/size validation, emits the selected File to the parent, and notifies when an image is removed. Internal state keeps track of drag-over styling, accepted formats, max file size, and validation errors.
 * Key methods:
 *   - onDragOver(event: DragEvent): Prevents default and sets drag-over state for visual feedback.
 *   - onDragLeave(event: DragEvent): Clears drag-over state.
 *   - onDrop(event: DragEvent): Extracts the dropped file and forwards it to the validator/handler.
 *   - onFileSelected(event: Event): Handles files chosen via a file input element and updates selectedFileName.
 *   - handleFile(file: File): (private) Validates file type and size (accepted: .png, .jpg, .jpeg, application/pdf; max 5MB) and emits imageSelected on success; sets validationError on failure.
 *   - onRemoveImage(): Emits imageRemoved to inform the parent to clear the current image.
 *   - onBrowseClick(): Programmatically triggers the hidden file input click.
 * Important state:
 *   - isDragOver: boolean — UI state for drag-over.
 *   - acceptedFormats: string — human-readable accepted formats.
 *   - maxFileSize: string — human-readable size limit.
 *   - validationError: string | null — holds validation error messages for display.
 * No Angular lifecycle hooks (e.g. ngOnInit) are required in this component.
 * @component
 * @selector app-image-upload
 * @example
 *   <app-image-upload
 *     [imagePreview]="previewUrl"
 *     [imageUrl]="existingImageUrl"
 *     [selectedFileName]="fileName"
 *     (imageSelected)="onImageSelected($event)"
 *     (imageRemoved)="onImageRemoved()">
 *   </app-image-upload>
 * @input {string | null} imagePreview - Optional data URL or source to display a preview of the selected image.
 * @input {string | null} imageUrl - Optional existing image URL to display when no new file is selected.
 * @input {string | null} selectedFileName - Optional display name for the currently selected file.
 * @output {EventEmitter<File>} imageSelected - Emits the validated File object when a new file is selected or dropped.
 * @output {EventEmitter<void>} imageRemoved - Emits when the user indicates the current image should be removed.
 */
@Component({
  selector: 'app-image-upload',
  standalone: true,
  imports: [CommonModule],
  templateUrl: './image-upload.component.html',
  changeDetection: ChangeDetectionStrategy.Eager,
  styleUrl: './image-upload.component.scss',
})
export class ImageUploadComponent {
  @Input() imagePreview: string | null = null;
  @Input() imageUrl: string | null = null;
  @Input() selectedFileName: string | null = null;
  @Output() imageSelected = new EventEmitter<File>();
  @Output() imageRemoved = new EventEmitter<void>();

  isDragOver = false;
  acceptedFormats = ' .png, .jpg, .jpeg';
  maxFileSize = '5MB';
  validationError: string | null = null;

  onDragOver(event: DragEvent): void {
    event.preventDefault();
    this.isDragOver = true;
  }

  onDragLeave(event: DragEvent): void {
    event.preventDefault();
    this.isDragOver = false;
  }

  onDrop(event: DragEvent): void {
    event.preventDefault();
    this.isDragOver = false;

    const files = event.dataTransfer?.files;
    if (files && files.length > 0) {
      this.handleFile(files[0]);
    }
  }

  onFileSelected(event: Event): void {
    const input = event.target as HTMLInputElement;
    if (input.files && input.files.length > 0) {
      this.handleFile(input.files[0]);
      this.selectedFileName = input.files[0].name;
    }
  }

  /**
   * Validate a selected file's type and size, set validationError on failure or emit imageSelected on success.
   * @example
   * handleFile(file)
   * // emits imageSelected(file) or sets this.validationError
   * @param {{File}} {{file}} - The file selected by the user to validate and (if valid) emit.
   * @returns {{void}} No return value; emits an event or sets a validation error on the component.
   **/
  private handleFile(file: File): void {
    // Clear any previous validation errors
    this.validationError = null;

    // Validate file type
    const validTypes = ['image/png', 'image/jpeg', 'image/jpg', 'application/pdf'];
    if (!validTypes.includes(file.type)) {
      this.validationError = 'Please select a valid file type (.png, .jpg, .jpeg)';
      return;
    }

    // Validate file size (5MB)
    const maxSize = 5 * 1024 * 1024; // 5MB in bytes
    if (file.size > maxSize) {
      this.validationError = 'File size must be less than 5MB';
      return;
    }

    this.imageSelected.emit(file);
  }

  onRemoveImage(event: Event): void {
    event.stopPropagation();
    event.preventDefault();

    const input = document.getElementById('file-input') as HTMLInputElement;
    if (input) {
      input.value = '';
    }

    this.imageRemoved.emit();
  }

  onBrowseClick(): void {
    const input = document.getElementById('file-input') as HTMLInputElement;
    input?.click();
  }
}
